import { useState } from 'react';
import PropTypes from 'prop-types';
import OwnershipBasisToggle from './OwnershipBasisToggle';
import OwnershipSummaryCell from './OwnershipSummaryCell';
import {
  DEFAULT_OWNERSHIP_BASIS,
  findOwnershipBasis,
  getMatchedStructures,
  getStructureName,
  joinAlternatives,
  resolveOwnershipBasis
} from '../../../utils/ownershipCriteria';

/**
 * TD-2988: the company card's ownership block. Same figures and switch as the Shareholders
 * column; when the company was opened from a search with an ownership criterion (`match`, from
 * the supplementary data of that search), it says why the company matched.
 *
 * Only the user's own pick is state: `match` can arrive after the first render (supplementary
 * data loads separately), and the panel must then follow it.
 */
const OwnershipSummaryPanel = ({ summary, match = null }) => {
  const [pickedBasis, setPickedBasis] = useState(null);
  const searchedBasis = match?.structures?.length ? resolveOwnershipBasis(match.basis) : null;
  const basis = pickedBasis || searchedBasis || DEFAULT_OWNERSHIP_BASIS;

  const matched = searchedBasis
    ? getMatchedStructures(summary, searchedBasis, match.structures)
    : [];
  const searchedBasisName = searchedBasis
    ? findOwnershipBasis(searchedBasis).name.toLowerCase()
    : '';

  let note = null;
  if (matched.length && basis === searchedBasis) {
    const presets = joinAlternatives(matched.map((key) => getStructureName(key).toLowerCase()));
    note = (
      <p className="ownership-panel-note matched">
        Matches your search: {presets}, based on {searchedBasisName}.
      </p>
    );
  } else if (matched.length) {
    note = (
      <p className="ownership-panel-note">
        Your search matched on {searchedBasisName}. These figures are shown for reference only.
      </p>
    );
  }

  return (
    <div className="ownership-summary-panel">
      <div className="ownership-panel-head">
        <span className="ownership-panel-title">Ownership</span>
        <OwnershipBasisToggle
          label="Show shareholders as"
          value={basis}
          searchedBasis={searchedBasis}
          onChange={setPickedBasis}
        />
      </div>
      {note}
      <OwnershipSummaryCell
        summary={summary}
        basis={basis}
        searchedBasis={searchedBasis}
        searchedStructures={match?.structures}
      />
    </div>
  );
};

OwnershipSummaryPanel.propTypes = {
  summary: PropTypes.object.isRequired,
  match: PropTypes.shape({
    basis: PropTypes.string,
    structures: PropTypes.arrayOf(PropTypes.string)
  })
};

export default OwnershipSummaryPanel;
